import {
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  JulianDate,
  Color,
  CallbackProperty,
  Cartographic,
  Ellipsoid,
  Rectangle,
  Math as CesiumMath,
  Cartesian3,
  ConstantProperty,
  PropertyBag,
} from "cesium";

import Type from "../Types";
import { getUsernameAndUserIdFormat, showToast } from "../Utility";

const timeNow = () => JulianDate.now();

const normalizeKey = (raw) =>
  String(raw ?? "")
    .toLowerCase()
    .replace(/[\s\-_]/g, "");

const getEntityOwnerDataSource = (viewer, entity) => {
  const owner = entity?.entityCollection?.owner;
  if (owner) return owner;

  const dsc = viewer?.dataSources;
  if (!dsc) return null;

  for (let i = 0; i < dsc.length; i++) {
    const ds = dsc.get(i);
    if (ds?.entities?.contains?.(entity)) return ds;
  }
  return null;
};

const getNormalizedLayerKey = (viewer, entity) => {
  const ds = getEntityOwnerDataSource(viewer, entity);
  const nm = ds?.layerName ?? ds?.name ?? "";
  return normalizeKey(nm);
};

const cartographicInsideRectangle = (rect, lon, lat) => {
  return lon >= rect.west && lon <= rect.east && lat >= rect.south && lat <= rect.north;
};

// Cohen–Sutherland (inclusive edges) for line segment vs axis-aligned rect in radians
const _outCode = (x, y, r) => {
  let c = 0;
  if (x < r.west) c |= 1;
  else if (x > r.east) c |= 2;
  if (y < r.south) c |= 4;
  else if (y > r.north) c |= 8;
  return c;
};

const segmentIntersectsRectangleRadians = (rect, x1, y1, x2, y2) => {
  if (cartographicInsideRectangle(rect, x1, y1) || cartographicInsideRectangle(rect, x2, y2)) return true;

  let oc1 = _outCode(x1, y1, rect);
  let oc2 = _outCode(x2, y2, rect);

  while (true) {
    if ((oc1 | oc2) === 0) return true;
    if ((oc1 & oc2) !== 0) return false;

    const out = oc1 !== 0 ? oc1 : oc2;
    let x = 0;
    let y = 0;

    const dx = x2 - x1;
    const dy = y2 - y1;

    if (out & 8) {
      if (dy === 0) return false;
      x = x1 + (dx * (rect.north - y1)) / dy;
      y = rect.north;
    } else if (out & 4) {
      if (dy === 0) return false;
      x = x1 + (dx * (rect.south - y1)) / dy;
      y = rect.south;
    } else if (out & 2) {
      if (dx === 0) return false;
      y = y1 + (dy * (rect.east - x1)) / dx;
      x = rect.east;
    } else {
      if (dx === 0) return false;
      y = y1 + (dy * (rect.west - x1)) / dx;
      x = rect.west;
    }

    if (out === oc1) {
      x1 = x;
      y1 = y;
      oc1 = _outCode(x1, y1, rect);
    } else {
      x2 = x;
      y2 = y;
      oc2 = _outCode(x2, y2, rect);
    }
  }
};

const windowPosToCartesian = (viewer, winPos) => {
  if (!viewer || !winPos) return null;
  const ray = viewer.camera.getPickRay(winPos);
  const globe = viewer.scene.globe;
  return globe?.pick?.(ray, viewer.scene) || viewer.camera.pickEllipsoid(winPos, Ellipsoid.WGS84);
};

const isEntityVisible = (entity) => {
  if (!entity) return false;
  if (entity.show === false) return false;
  const owner = entity?.entityCollection?.owner;
  if (owner && owner.show === false) return false;
  return true;
};

// Fast, no allocations: checks entity position / polyline / polygon against rect
const entityIntersectsRect = (entity, rect, t, curCarto) => {
  const ellipsoid = Ellipsoid.WGS84;

  const posProp = entity?.position;
  if (posProp && typeof posProp.getValue === "function") {
    const c = posProp.getValue(t);
    if (c) {
      const carto = ellipsoid.cartesianToCartographic(c, curCarto);
      if (carto && cartographicInsideRectangle(rect, carto.longitude, carto.latitude)) return true;
    }
  }

  const scanPositions = (positions) => {
    if (!Array.isArray(positions) || positions.length === 0) return false;

    let hasPrev = false;
    let prevLon = 0;
    let prevLat = 0;

    for (let i = 0; i < positions.length; i++) {
      const p = positions[i];
      if (!p) continue;

      const carto = ellipsoid.cartesianToCartographic(p, curCarto);
      if (!carto) continue;

      const lon = carto.longitude;
      const lat = carto.latitude;

      if (cartographicInsideRectangle(rect, lon, lat)) return true;

      if (hasPrev && segmentIntersectsRectangleRadians(rect, prevLon, prevLat, lon, lat)) return true;

      prevLon = lon;
      prevLat = lat;
      hasPrev = true;
    }

    return false;
  };

  const ph = entity?.polygon?.hierarchy;
  if (ph && typeof ph.getValue === "function") {
    try {
      const hierarchy = ph.getValue(t);
      const positions = hierarchy?.positions || hierarchy;
      if (scanPositions(positions)) return true;
    } catch {}
  }

  const pl = entity?.polyline?.positions;
  if (pl && typeof pl.getValue === "function") {
    try {
      const positions = pl.getValue(t);
      if (scanPositions(positions)) return true;
    } catch {}
  }

  return false;
};

// Only scans allowed dataSources + streaming intersection test
const getAllowedEntitiesInRectangle = (viewer, rect, isAllowedDataSource) => {
  const hits = [];
  const dsc = viewer?.dataSources;
  if (!dsc) return hits;

  const t = timeNow();
  const curCarto = new Cartographic();

  for (let i = 0; i < dsc.length; i++) {
    const ds = dsc.get(i);
    if (!ds || !isAllowedDataSource(ds)) continue;

    const ents = ds?.entities?.values;
    if (!Array.isArray(ents) || ents.length === 0) continue;

    for (let j = 0; j < ents.length; j++) {
      const e = ents[j];
      if (!isEntityVisible(e)) continue;
      if (entityIntersectsRect(e, rect, t, curCarto)) hits.push(e);
    }
  }

  return hits;
};

const horizontalize = (v, up, out) => {
  const d = Cartesian3.dot(v, up);
  const proj = Cartesian3.multiplyByScalar(up, d, out);
  return Cartesian3.subtract(v, proj, out);
};

const maxTurningAngleDeg = (positions) => {
  if (!Array.isArray(positions) || positions.length < 3) return 0;

  let maxDeg = 0;

  const v1 = new Cartesian3();
  const v2 = new Cartesian3();
  const up = new Cartesian3();
  const a = new Cartesian3();
  const b = new Cartesian3();

  for (let i = 1; i < positions.length - 1; i++) {
    const p0 = positions[i - 1];
    const p1 = positions[i];
    const p2 = positions[i + 1];
    if (!p0 || !p1 || !p2) continue;

    Cartesian3.subtract(p1, p0, v1);
    Cartesian3.subtract(p2, p1, v2);

    Ellipsoid.WGS84.geodeticSurfaceNormal(p1, up);

    horizontalize(v1, up, a);
    horizontalize(v2, up, b);

    if (Cartesian3.magnitudeSquared(a) < 1e-6 || Cartesian3.magnitudeSquared(b) < 1e-6) continue;

    Cartesian3.normalize(a, a);
    Cartesian3.normalize(b, b);

    const dot = CesiumMath.clamp(Cartesian3.dot(a, b), -1.0, 1.0);
    const rad = Math.acos(dot);
    const deg = CesiumMath.toDegrees(rad);

    if (deg > maxDeg) maxDeg = deg;
  }

  return maxDeg;
};

const buildRightOffsetPolyline = (positions, offsetMeters) => {
  if (!Array.isArray(positions) || positions.length < 2) return null;

  const outPositions = [];
  const up = new Cartesian3();
  const t = new Cartesian3();
  const th = new Cartesian3();
  const right = new Cartesian3();
  const tmp = new Cartesian3();

  for (let i = 0; i < positions.length; i++) {
    const p = positions[i];
    if (!p) continue;

    let dir;
    if (i === 0) {
      dir = Cartesian3.subtract(positions[1], positions[0], t);
    } else if (i === positions.length - 1) {
      dir = Cartesian3.subtract(positions[i], positions[i - 1], t);
    } else {
      dir = Cartesian3.subtract(positions[i + 1], positions[i - 1], t);
    }

    Ellipsoid.WGS84.geodeticSurfaceNormal(p, up);

    horizontalize(dir, up, th);
    if (Cartesian3.magnitudeSquared(th) < 1e-8) {
      outPositions.push(p);
      continue;
    }

    Cartesian3.normalize(th, th);

    Cartesian3.cross(th, up, right);
    if (Cartesian3.magnitudeSquared(right) < 1e-8) {
      outPositions.push(p);
      continue;
    }

    Cartesian3.normalize(right, right);
    Cartesian3.multiplyByScalar(right, offsetMeters, tmp);
    outPositions.push(Cartesian3.add(p, tmp, new Cartesian3()));
  }

  return outPositions.length >= 2 ? outPositions : null;
};

const getEntityPositions = (entity) => {
  const t = timeNow();
  const result = [];

  const posProp = entity?.position;
  if (posProp && typeof posProp.getValue === "function") {
    const c = posProp.getValue(t);
    if (c) result.push(c);
  }

  const ph = entity?.polygon?.hierarchy;
  if (ph && typeof ph.getValue === "function") {
    try {
      const hierarchy = ph.getValue(t);
      const positions = hierarchy?.positions || hierarchy;
      if (Array.isArray(positions)) {
        for (let i = 0; i < positions.length; i++) {
          const p = positions[i];
          if (p) result.push(p);
        }
      }
    } catch {}
  }

  const pl = entity?.polyline?.positions;
  if (pl && typeof pl.getValue === "function") {
    try {
      const positions = pl.getValue(t);
      if (Array.isArray(positions)) {
        for (let i = 0; i < positions.length; i++) {
          const p = positions[i];
          if (p) result.push(p);
        }
      }
    } catch {}
  }

  return result;
};

const getNumericPropValue = (entity, propName) => {
  const pbag = entity?.properties;
  if (!pbag) return null;

  const prop = pbag[propName];
  if (!prop) return null;

  try {
    const v = typeof prop.getValue === "function" ? prop.getValue(timeNow()) : prop;
    const n = typeof v === "number" ? v : Number(String(v).trim());
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

const getNextIdPlus2 = (ds) => {
  const ents = ds?.entities?.values;
  if (!Array.isArray(ents) || ents.length === 0) return 2;

  let maxId = 0;
  for (let i = 0; i < ents.length; i++) {
    const e = ents[i];
    const n = getNumericPropValue(e, "id");
    if (n != null && n > maxId) maxId = n;
  }

  const next = maxId + 2;
  return Number.isFinite(next) ? next : 2;
};

export const createAutoSketcher = (viewer, targetDataSource, opts = {}) => {
  const options = {
    offsetMeters: 0.2,
    maxCurvatureDeg: 8,
    layerNameAllowList: ["curbstone", "road edge", "concrete barrier", "guardrail"],
    onCurvatureReject: null,
    onDone: null,
    ...opts,
  };

  const pickPolylineStyleFromDataSource = (ds) => {
    const t = timeNow();
    const ents = ds?.entities?.values;
    if (!Array.isArray(ents) || ents.length === 0) return null;

    const first = ents.find((e) => e?.polyline);
    if (!first?.polyline) return null;

    const pl = first.polyline;

    return {
      width: typeof pl.width?.getValue === "function" ? pl.width.getValue(t) : pl.width,
      clampToGround: typeof pl.clampToGround?.getValue === "function" ? pl.clampToGround.getValue(t) : pl.clampToGround,
      material: pl.material,
      arcType: typeof pl.arcType?.getValue === "function" ? pl.arcType.getValue(t) : pl.arcType,
      zIndex: typeof pl.zIndex?.getValue === "function" ? pl.zIndex.getValue(t) : pl.zIndex,
    };
  };

  const allowKeys = options.layerNameAllowList.map(normalizeKey);

  // caches (speed only)
  const dsKeyCache = new WeakMap();
  const dsAllowedCache = new WeakMap();
  const entityAllowedCache = new WeakMap();

  const getDsKey = (ds) => {
    if (dsKeyCache.has(ds)) return dsKeyCache.get(ds);
    const key = normalizeKey(ds?.layerName ?? ds?.name ?? "");
    dsKeyCache.set(ds, key);
    return key;
  };

  const isAllowedDataSource = (ds) => {
    if (dsAllowedCache.has(ds)) return dsAllowedCache.get(ds);
    const key = getDsKey(ds);
    const ok = !!key && allowKeys.some((a) => key.includes(a) || a.includes(key));
    dsAllowedCache.set(ds, ok);
    return ok;
  };

  const isAllowedLayer = (viewer2, entity) => {
    if (entityAllowedCache.has(entity)) return entityAllowedCache.get(entity);
    const key = getNormalizedLayerKey(viewer2, entity);
    const ok = !!key && allowKeys.some((a) => key.includes(a) || a.includes(key));
    entityAllowedCache.set(entity, ok);
    return ok;
  };

  if (!viewer?.scene?.canvas) return { destroy: () => {}, processOneEntity: () => ({ ok: false }) };
  if (!targetDataSource?.entities) return { destroy: () => {}, processOneEntity: () => ({ ok: false }) };

  const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);

  const currentRect = new Rectangle();
  const startCarto = new Cartographic();
  const endCarto = new Cartographic();

  let dragStartPos = null;
  let isDragging = false;

  let liveRectEntity = null;
  const drawnRectEntities = [];

  const updateCurrentRectFromWindow = (p1, p2) => {
    const c1 = windowPosToCartesian(viewer, p1);
    const c2 = windowPosToCartesian(viewer, p2);
    if (!c1 || !c2) return false;

    const carto1 = Ellipsoid.WGS84.cartesianToCartographic(c1, startCarto);
    const carto2 = Ellipsoid.WGS84.cartesianToCartographic(c2, endCarto);

    currentRect.west = Math.min(carto1.longitude, carto2.longitude);
    currentRect.east = Math.max(carto1.longitude, carto2.longitude);
    currentRect.south = Math.min(carto1.latitude, carto2.latitude);
    currentRect.north = Math.max(carto1.latitude, carto2.latitude);

    return true;
  };

  const ensureLiveRectangle = () => {
    if (liveRectEntity) return;
    liveRectEntity = viewer.entities.add({
      rectangle: {
        coordinates: new CallbackProperty(() => currentRect, false),
        material: Color.YELLOW.withAlpha(0.15),
        outline: true,
        outlineColor: Color.YELLOW,
      },
    });
  };

  const removeLiveRectangle = () => {
    if (!liveRectEntity) return;
    viewer.entities.remove(liveRectEntity);
    liveRectEntity = null;
  };

  const removeAllDrawnRectangles = () => {
    for (let i = 0; i < drawnRectEntities.length; i++) {
      const ent = drawnRectEntities[i];
      try {
        viewer.entities.remove(ent);
      } catch {}
    }
    drawnRectEntities.length = 0;
    removeLiveRectangle();
  };

  const originalCameraControls = { current: null };

  const disableCameraControls = (viewer2) => {
    if (!viewer2?.scene?.screenSpaceCameraController) return false;
    const c = viewer2.scene.screenSpaceCameraController;

    if (!originalCameraControls.current) {
      originalCameraControls.current = {
        enableRotate: c.enableRotate,
        enableTranslate: c.enableTranslate,
        enableZoom: c.enableZoom,
        enableTilt: c.enableTilt,
        enableLook: c.enableLook,
      };
    }

    c.enableRotate = false;
    c.enableTranslate = false;
    c.enableZoom = false;
    c.enableTilt = false;
    c.enableLook = false;
    return true;
  };

  const restoreCameraControls = (viewer2) => {
    if (!viewer2?.scene?.screenSpaceCameraController) return;
    const saved = originalCameraControls.current;
    if (!saved) return;

    const c = viewer2.scene.screenSpaceCameraController;
    c.enableRotate = saved.enableRotate;
    c.enableTranslate = saved.enableTranslate;
    c.enableZoom = saved.enableZoom;
    c.enableTilt = saved.enableTilt;
    c.enableLook = saved.enableLook;
    originalCameraControls.current = null;
  };

  const onWindowMouseUp = () => {
    if (!dragStartPos) return;
    dragStartPos = null;
    isDragging = false;
    restoreCameraControls(viewer);
    removeLiveRectangle();
    if (viewer?.scene?.requestRenderMode) viewer.scene.requestRender();
  };

  if (typeof window !== "undefined") {
    window.addEventListener("mouseup", onWindowMouseUp);
  }

  const baseStyle = pickPolylineStyleFromDataSource(targetDataSource);
  const creator = getUsernameAndUserIdFormat(opts?.userData);
  let nextNumericId = getNextIdPlus2(targetDataSource);

  // count of newly created entities (per finalize run)
  let createdCount = 0;

  const addOffsetPolylineToTarget = (offsetPositions) => {
    const props = new PropertyBag({});
    props.addProperty?.("id");
    props.id = new ConstantProperty(nextNumericId);
    if (creator) {
      props.addProperty?.("creator");
      props.creator = new ConstantProperty(creator);
    }

    targetDataSource.entities.add({
      properties: props,
      polyline: {
        positions: offsetPositions,
        width: baseStyle?.width ?? 2,
        material: baseStyle?.material ?? Color.CYAN,
        clampToGround: baseStyle?.clampToGround ?? true,
        arcType: baseStyle?.arcType,
        zIndex: baseStyle?.zIndex,
      },
    });

    nextNumericId += 2;
    createdCount += 1;
  };

  const processOneEntity = (entity, { force = false } = {}) => {
    if (!entity) return { ok: false, reason: "no_entity" };

    if (!isAllowedLayer(viewer, entity)) return { ok: false, reason: "not_allowed_layer" };

    const positions = getEntityPositions(entity);
    if (!Array.isArray(positions) || positions.length < 2) return { ok: false, reason: "no_positions" };

    const maxDeg = maxTurningAngleDeg(positions);
    if (!force && maxDeg > options.maxCurvatureDeg) {
      return { ok: false, reason: "curvature_reject", maxDeg };
    }

    const offsetPositions = buildRightOffsetPolyline(positions, options.offsetMeters);
    if (!offsetPositions) return { ok: false, reason: "offset_failed" };

    addOffsetPolylineToTarget(offsetPositions);
    return { ok: true, maxDeg };
  };

  const finalizeAndProcess = (finalRect) => {
    createdCount = 0;

    // optimized: scan only allowed dataSources + no temp arrays during hit-test
    const matched = getAllowedEntitiesInRectangle(viewer, finalRect, isAllowedDataSource);

    const rejected = [];
    for (let i = 0; i < matched.length; i++) {
      const e = matched[i];
      const res = processOneEntity(e, { force: false });
      if (!res.ok && res.reason === "curvature_reject") rejected.push(e);
    }

    if (createdCount > 0) {
      showToast(`${createdCount} new entities were created.`, Type.NOTIFICATION.SUCCESS, { duration: 4000 });
    }

    if (typeof options.onCurvatureReject === "function" && rejected.length > 0) {
      options.onCurvatureReject(rejected);
    }

    removeAllDrawnRectangles();
    destroy();

    if (typeof options.onDone === "function") {
      options.onDone({ matchedCount: matched.length, rejectedCount: rejected.length });
    }
  };

  handler.setInputAction((movement) => {
    const ok = disableCameraControls(viewer);
    if (!ok) return;
    dragStartPos = movement?.position || movement;
    isDragging = false;
  }, ScreenSpaceEventType.LEFT_DOWN);

  handler.setInputAction((movement) => {
    if (!dragStartPos) return;
    const cur = movement?.endPosition || movement;

    if (!isDragging && Math.hypot(cur.x - dragStartPos.x, cur.y - dragStartPos.y) > 5) isDragging = true;
    if (!isDragging) return;

    ensureLiveRectangle();
    if (updateCurrentRectFromWindow(dragStartPos, cur) && viewer.scene.requestRenderMode) {
      viewer.scene.requestRender();
    }
  }, ScreenSpaceEventType.MOUSE_MOVE);

  handler.setInputAction((movement) => {
    const wasDragging = isDragging;
    const endPos = movement?.position || movement;

    let finalRect = null;
    if (wasDragging && updateCurrentRectFromWindow(dragStartPos, endPos)) {
      finalRect = Rectangle.fromRadians(currentRect.west, currentRect.south, currentRect.east, currentRect.north);
    }

    dragStartPos = null;
    isDragging = false;

    restoreCameraControls(viewer);
    removeLiveRectangle();

    if (!wasDragging || !finalRect) return;

    const rectEnt = viewer.entities.add({
      rectangle: {
        coordinates: finalRect,
        material: Color.ORANGE.withAlpha(0.12),
        outline: true,
        outlineColor: Color.ORANGE,
      },
    });
    drawnRectEntities.push(rectEnt);

    if (viewer.scene.requestRenderMode) viewer.scene.requestRender();

    finalizeAndProcess(finalRect);
  }, ScreenSpaceEventType.LEFT_UP);

  const destroy = () => {
    try {
      handler?.destroy();
    } catch {}
    try {
      removeAllDrawnRectangles();
    } catch {}
    try {
      restoreCameraControls(viewer);
    } catch {}
    try {
      if (typeof window !== "undefined") window.removeEventListener("mouseup", onWindowMouseUp);
    } catch {}
    dragStartPos = null;
    isDragging = false;
  };

  return { destroy, processOneEntity };
};
